import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useEffect, useRef } from 'react';

const ENTITY = 'OperationalExpense';

const BANK_ACCOUNT_NAME_MAP = {
  bca: 'Apa Saja Bisa',
  bni: 'Satala Dermatech Essential',
  mandiri: 'Cuan Jalan Terus'
};

export function useOperationalExpenses() {
  const qc = useQueryClient();

  const { data: expenses = [], isLoading, refetch } = useQuery({
    queryKey: ['operational-expenses'],
    queryFn: () => base44.entities[ENTITY].list('-created_date'),
    staleTime: 5000,
  });

  const createMut = useMutation({
    mutationFn: async (data) => {
      const created = await base44.entities[ENTITY].create(data);
      try {
        const refId = `opex-${created.id}`;
        const newDescription = `Biaya Operasional: ${created.category} - ${created.description} (${created.requester || 'Office'})`;
        const bankKey = (created.bankAccountId || data.bankAccountId || 'bca').toLowerCase();
        const accountName = BANK_ACCOUNT_NAME_MAP[bankKey] || 'Apa Saja Bisa';
        await base44.entities.AccountMutation.create({
          date: created.date,
          type: 'keluar',
          account: accountName,
          amount: created.amount,
          description: newDescription,
          reference_id: refId,
          coa_allocations: created.coa_allocations || []
        });
      } catch (err) {
        console.error('Gagal push mutasi opex:', err);
      }
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-expenses'] });
      qc.invalidateQueries({ queryKey: ['account-mutations'] });
    },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, ...data }) => {
      const updated = await base44.entities[ENTITY].update(id, data);
      try {
        const refId = `opex-${id}`;
        const existing = await base44.entities.AccountMutation.filter({ reference_id: refId });
        if (existing && existing.length > 0) {
          const bankKey = (updated.bankAccountId || data.bankAccountId || 'bca').toLowerCase();
          const accountName = BANK_ACCOUNT_NAME_MAP[bankKey] || 'Apa Saja Bisa';
          await base44.entities.AccountMutation.update(existing[0].id, {
            date: updated.date,
            amount: updated.amount,
            account: accountName,
            description: `Biaya Operasional: ${updated.category} - ${updated.description} (${updated.requester || 'Office'})`
          });
        }
      } catch (err) {
        console.error('Gagal update mutasi opex:', err);
      }
      return updated;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-expenses'] });
      qc.invalidateQueries({ queryKey: ['account-mutations'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => {
      const res = await base44.entities[ENTITY].hardDelete(id);
      try {
        const refId = `opex-${id}`;
        const existing = await base44.entities.AccountMutation.filter({ reference_id: refId });
        if (existing && existing.length > 0) {
          await base44.entities.AccountMutation.hardDelete(existing[0].id);
        }
      } catch (err) {
        console.error('Gagal delete mutasi opex:', err);
      }
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-expenses'] });
      qc.invalidateQueries({ queryKey: ['account-mutations'] });
    },
  });

  return {
    expenses,
    isLoading,
    refetch,
    createExpense: createMut.mutateAsync,
    updateExpense: (id, data) => updateMut.mutateAsync({ id, ...data }),
    deleteExpense: deleteMut.mutateAsync,
    // Convenience: replace all expenses (for bulk save)
    replaceAll: async (newList) => {
      // Delete all existing then re-create
      await Promise.all(expenses.map(e => base44.entities[ENTITY].hardDelete(e.id)));
      await Promise.all(newList.map(item => base44.entities[ENTITY].create(item)));
      qc.invalidateQueries({ queryKey: ['operational-expenses'] });
    }
  };
}
